import {
  MEMBERSHIP_DEMO_URL,
  MEMBERSHIP_PRICING_TIERS,
  MEMBERSHIP_SIGNUP_URL,
} from '../../constants/membershipPricingConstants';

/** Membership application intake (Phase 1). */
export { MEMBERSHIP_SIGNUP_URL, MEMBERSHIP_DEMO_URL };

export const HERO_HEADLINE = 'California Climate Action Portal';

export const HERO_SUBHEADLINE =
  'GHG inventories, key progress indicators, policy analysis, community engagement tools, reporting, and support for California local governments.';

export const WORKFLOW_SECTION = {
  title: 'Filling the Gaps to Advance Climate Action',
  subtitle: 'A complete workflow for local governments — from understanding conditions through implementation.',
} as const;

export const WORKFLOW_COLUMNS = [
  {
    title: 'Understand',
    items: ['GHG inventories', 'Equity indicators', 'Peer benchmarking', 'Community profiles'],
  },
  {
    title: 'Prioritize',
    items: ['Scenario modeling', 'Policy evaluation', 'Peer comparisons', 'Climate action planning'],
  },
  {
    title: 'Implement',
    items: ['Reports', 'Community engagement', 'Progress tracking', 'Funding support'],
  },
] as const;

export const MEMBERSHIP_HERO = {
  title: 'Spend Less on Climate Planning. Invest More in Climate Action.',
  paragraphs: [
    'ClimatePlans is a living climate planning and implementation platform for California local governments.',
    'Instead of recreating inventories, reports, benchmarks, and funding research every few years, ClimatePlans keeps these resources continuously updated so agencies and consultants can focus on policy decisions, community engagement, and implementation.',
  ],
  goalCallout: {
    label: 'Our Goal',
    prefix: 'Help members save ',
    emphasis: 'at least $10 in planning costs for every $1 invested in membership',
    suffix:
      ' by reducing repetitive planning work and redirecting staff time toward implementation.',
  },
} as const;

export const NEEDS_ASSESSMENT_PDF_URL = '/docs/CoolClimate-Needs-Assessment-2025.pdf';
export const NEEDS_ASSESSMENT_SOURCE_LABEL = 'CoolClimate Network Needs Assessment, 2025';

export const MEMBERSHIP_COST_COMPARISON = {
  intro:
    'Traditional climate planning often requires tens of thousands of dollars for greenhouse gas inventories and hundreds of thousands for Climate Action Plans. Annual membership is typically only a small fraction of those costs while providing continuous access to data, reporting, benchmarking, funding intelligence, and implementation tools.',
  disclaimer:
    'These comparisons are not savings guarantees. They show that annual membership is generally a fraction of what responding jurisdictions reported spending on one GHG inventory or CAP. ClimatePlans supports recurring data, reporting, engagement, benchmarking, and implementation workflows; local tailoring remains essential.',
  columns: [
    'Population',
    'Avg. reported GHG inventory cost',
    'Affiliate membership',
    'Affiliate as % of one inventory',
    'Avg. reported CAP cost',
    'Regular membership',
    'Regular as % of one CAP',
  ],
  rows: MEMBERSHIP_PRICING_TIERS.rows,
} as const;

export const BETTER_STARTING_POINT = {
  title: 'A Better Starting Point',
  paragraphs: [
    'ClimatePlans does not replace local governments or consultants.',
    'Local governments still establish priorities, engage residents, and adopt policies. Consultants continue providing facilitation, technical expertise, local analysis, and implementation support.',
    'ClimatePlans automates the repetitive work—maintaining statewide data, greenhouse gas inventories, benchmarking, funding opportunities, reporting, and implementation metrics—so everyone can spend more time creating successful climate projects.',
  ],
} as const;

export type WorkflowStep = {
  icon: string;
  text: string;
};

export const WORKFLOW_COMPARISON = {
  title: 'Two Ways to Do the Work',
  traditional: {
    label: 'Traditional workflow',
    items: [
      { icon: '🔍', text: 'Search for funding opportunities' },
      { icon: '👥', text: 'Hire consultant' },
      { icon: '📁', text: 'Collect data' },
      { icon: '📊', text: 'Produce greenhouse gas inventory' },
      { icon: '📝', text: 'Draft Climate Action Plan' },
      { icon: '📄', text: 'Publish static PDF' },
      { icon: '🔁', text: 'Repeat every few years' },
    ] satisfies WorkflowStep[],
  },
  climatePlans: {
    label: 'ClimatePlans workflow',
    items: [
      { icon: '🗺️', text: 'Continuously updated statewide data' },
      { icon: '⚡', text: 'Generate inventory reports automatically' },
      { icon: '📈', text: 'Benchmark against California peers' },
      { icon: '💡', text: 'Identify funding opportunities' },
      { icon: '🤝', text: 'Engage households, businesses, and local governments' },
      { icon: '📋', text: 'Track implementation progress' },
      { icon: '🔄', text: 'Update planning continuously' },
    ] satisfies WorkflowStep[],
  },
} as const;

export const PUBLIC_ACCESS = {
  tierLabel: 'Public Access',
  heading: 'Explore California Climate Data — Free',
  description:
    'Public access lets anyone explore California communities, compare jurisdictions, and understand local climate conditions before becoming a member.',
  items: [
    'Explore every California community',
    'Compare jurisdictions',
    'View indicators and GHG inventories',
    'Discover funding opportunities',
    'Access public dashboards',
  ],
} as const;

export const AFFILIATE_MEMBERSHIP = {
  tierLabel: 'Affiliate Membership',
  heading: 'Inventory, Reporting & Benchmarking',
  priceLabel: 'Starting at $2,500/year',
  intro: 'Downloadable reports, statewide benchmarking, and engagement tools for inventory and reporting workflows.',
  items: [
    'Downloadable GHG inventory reports',
    'Data and figure downloads',
    'ICLEI-compatible reporting',
    'Statewide peer benchmarking',
    'Member webinars and working groups',
    'Climate intelligence updates',
    'Household and business calculator license',
    'Local Government Workspace',
    'Recognition as a CoolClimate Network member',
  ],
} as const;

export const REGULAR_MEMBERSHIP = {
  tierLabel: 'Regular Membership',
  heading: 'Planning & Implementation',
  priceLabel: 'Starting at $5,000/year',
  intro: 'Everything in Affiliate Membership, plus:',
  items: [
    'Climate Planning Workspace',
    'Living implementation tracking',
    'Funding opportunity matching',
    'Policy and measure library',
    'Custom dashboards',
    'Community engagement tools',
    'Organization management',
    'Staff training',
    'Technical support',
  ],
} as const;

export const MEMBERSHIP_ROADMAP_NOTE =
  'Future updates will include tools that help agencies and consultants develop draft Climate Action Plans more efficiently.';

export const WHY_AGENCIES_JOIN = {
  title: 'Why Agencies Join',
  items: [
    'Reduce recurring planning costs',
    'Complete inventories in days instead of months',
    'Reuse data across reports',
    'Discover funding opportunities faster',
    'Benchmark against every California jurisdiction',
    'Spend more time implementing projects',
  ],
} as const;

export const FEATURED_TOOLS_SECTION = {
  title: 'CoolClimate Member Resources',
  subtitle: 'Member tools for analysis, reporting, engagement, and jurisdiction-specific customization.',
} as const;

export type FeaturedToolMembershipLevel = 'free' | 'affiliate' | 'regular';

export const FEATURED_TOOL_MEMBERSHIP_LABELS: Record<FeaturedToolMembershipLevel, string> = {
  free: 'Free Level',
  affiliate: 'Affiliate Level',
  regular: 'Regular Level',
};

export const FEATURED_MEMBER_TOOLS = [
  {
    id: 'climateplans',
    title: 'ClimatePlans Basic',
    description: [
      'Over 100 free indicators for every California city and county',
      'Data, figures and custom narrative',
    ],
    href: '/',
    membershipLevel: 'free',
    isCurrentApp: true,
  },
  {
    id: 'advanced-analysis',
    title: 'Analysis and Reports',
    description: [
      'Statewide analysis & benchmarking',
      'Downloadable GHG inventory & data reports',
    ],
    href: '/law-impact',
    membershipLevel: 'affiliate',
  },
  {
    id: 'local-government-workspace',
    title: 'Local Government Workspace',
    description: [
      'Customize data, chart layout and narrative',
      'Add custom data and figures',
      'Local admin privileges',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'affiliate',
  },
  {
    id: 'data-download',
    title: 'Data & Figure Download',
    description: 'Export charts, tables, and datasets for reports and presentations.',
    href: '#public-vs-membership',
    membershipLevel: 'affiliate',
  },
  {
    id: 'calculator',
    title: 'CoolClimate Household Calculator',
    description: 'Community engagement',
    href: process.env.REACT_APP_CALCULATOR_URL || 'https://coolclimate.org/calculator',
    membershipLevel: 'affiliate',
    external: true,
  },
  {
    id: 'business',
    title: 'CoolClimate Calculator for Businesses',
    description: 'Organizational emissions',
    href: process.env.REACT_APP_BUSINESS_URL || 'https://coolclimate.org/org-calc',
    membershipLevel: 'affiliate',
    external: true,
  },
  {
    id: 'custom-portal',
    title: 'Custom Climate Action Portal',
    description:
      'A fully customized portal tailored to your jurisdiction\'s goals and workflows. You design it. We build it.',
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
  {
    id: 'consumption-based-ghg-inventory',
    title: 'Consumption-Based GHG Inventory',
    description: [
      'Tract-level CBEI',
      'Detailed sector-level analysis',
      'Local, domestic and international emissions',
      'Target setting',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
  {
    id: 'scenario-planning',
    title: 'Scenario Planning',
    description: [
      'Explore GHG mitigation policy scenarios',
      'Estimate impacts and costs of future actions',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
  {
    id: 'policy-explorer',
    title: 'Policy Explorer',
    description: [
      'Review local ordinances, climate action plans, and general plans',
      'Compare policies from peer jurisdictions',
      'Support policy drafting with examples and templates',
    ],
    href: '/law-impact',
    membershipLevel: 'regular',
  },
  {
    id: 'challenge',
    title: 'Cool Campus Challenge',
    description: 'Campus engagement and behavior change',
    href: process.env.REACT_APP_CHALLENGE_URL || 'https://coolcampuschallenge.org/',
    membershipLevel: 'regular',
    external: true,
  },
  {
    id: 'advanced-local-government-admin',
    title: 'Advanced Local Government Admin',
    description: [
      'Dashboard of actions taken by households and residents',
      'Create & manage your own custom Challenges',
      'Assign and manage content from local users',
      'Messaging / contact system for all local users',
    ],
    href: '#public-vs-membership',
    membershipLevel: 'regular',
  },
] as const;

export const SUCCESS_STORIES_SECTION = {
  title: 'Success Stories',
  subtitle: 'How California communities use CoolClimate tools for planning and implementation.',
} as const;

export const SUCCESS_STORIES = [
  {
    id: 'coolcalifornia',
    title: 'CoolCalifornia',
    excerpt:
      '20+ years working in partnership with the California Air Resources Board to develop tools for California residents, businesses, schools, and communities.',
    isComingSoon: false,
  },
  {
    id: 'san-francisco',
    title: 'City and County of San Francisco',
    excerpt:
      'Developed industry-leading consumption-based GHG inventory methodology and is implementing a city government employee engagement competition.',
    isComingSoon: false,
  },
  {
    id: 'more-stories',
    title: 'More member stories coming soon',
    excerpt:
      'Future case studies will highlight how cities, counties, campuses, and organizations use CoolClimate tools for planning and implementation.',
    isComingSoon: true,
  },
] as const;

export const WHY_COOLCLIMATE_SECTION = {
  title: 'Why UC Berkeley\u2019s CoolClimate Network?',
  items: [
    '20+ years developing leading climate research and software for California',
    'Partner on CoolCalifornia.org',
    'Winner of a $1.3M grant funded by the California Legislature to develop ClimatePlans.org',
    'Technical advisory board with input from dozens of experts',
    'Developer of the CoolClimate Calculator with millions of users',
    '10+ years running behavior-based competitions to engage California residents, universities, and businesses',
  ],
} as const;

export const FINAL_CTA = {
  title: 'Join California\u2019s Climate Planning Network',
  text: 'Cities, counties, campuses, and organizations use CoolClimate tools for inventory, benchmarking, engagement, and implementation support.',
} as const;
